import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Id } from '../../../convex/_generated/dataModel';

interface ReferenceSelectionContextType {
  selectedReferences: Set<Id<'references'>>;
  toggleSelection: (referenceId: Id<'references'>) => void;
  selectAll: (referenceIds: Id<'references'>[]) => void;
  clearSelection: () => void;
  isSelected: (referenceId: Id<'references'>) => boolean;
  selectionCount: number;
}

const ReferenceSelectionContext = createContext<ReferenceSelectionContextType | undefined>(undefined);

export function ReferenceSelectionProvider({ children }: { children: React.ReactNode }) {
  const [selectedReferences, setSelectedReferences] = useState<Set<Id<'references'>>>(new Set());

  const toggleSelection = useCallback((referenceId: Id<'references'>) => {
    setSelectedReferences((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(referenceId)) {
        newSet.delete(referenceId);
      } else {
        newSet.add(referenceId);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback((referenceIds: Id<'references'>[]) => {
    setSelectedReferences(new Set(referenceIds));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedReferences(new Set());
  }, []);

  const isSelected = useCallback((referenceId: Id<'references'>) => {
    return selectedReferences.has(referenceId);
  }, [selectedReferences]);

  const selectionCount = selectedReferences.size;

  return (
    <ReferenceSelectionContext.Provider
      value={{
        selectedReferences,
        toggleSelection,
        selectAll,
        clearSelection,
        isSelected,
        selectionCount,
      }}
    >
      {children}
    </ReferenceSelectionContext.Provider>
  );
}

export function useReferenceSelection() {
  const context = useContext(ReferenceSelectionContext);
  if (!context) {
    throw new Error('useReferenceSelection must be used within a ReferenceSelectionProvider');
  }
  return context;
}
